import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, IconButton, Tooltip } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicNoneIcon from "@mui/icons-material/MicNone";

// Minimal Web Speech API typings — not present in our lib.dom target.
interface SpeechRecognitionAlternativeLike {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  0: SpeechRecognitionAlternativeLike;
  isFinal: boolean;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

const TEXT_INPUT_TYPES = new Set(["text", "search", "email", "url", "tel", ""]);
type EditableField = HTMLInputElement | HTMLTextAreaElement | HTMLElement;

function isNativeField(el: EditableField): el is HTMLInputElement | HTMLTextAreaElement {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
}

function asEditableField(el: EventTarget | null): EditableField | null {
  if (el instanceof HTMLTextAreaElement) {
    return el.readOnly || el.disabled ? null : el;
  }
  if (el instanceof HTMLInputElement) {
    if (el.readOnly || el.disabled) return null;
    return TEXT_INPUT_TYPES.has(el.type) ? el : null;
  }
  if (el instanceof HTMLElement && el.isContentEditable) return el;
  return null;
}

// Whether the character immediately before the contenteditable caret is
// non-whitespace, so we know to prepend a separating space.
function ceCaretNeedsLeadingSpace(): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0).cloneRange();
  try {
    range.setStart(range.startContainer, Math.max(0, range.startOffset - 1));
    const prev = range.toString();
    return prev.length > 0 && !/\s/.test(prev);
  } catch {
    return false;
  }
}

// React tracks <input>/<textarea> values via a native setter; assigning .value
// directly does not fire onChange. Call the prototype setter, then dispatch a
// bubbling input event so controlled components update through their handler.
function setNativeValue(field: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto =
    field instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, "value")?.set?.call(field, value);
  field.dispatchEvent(new Event("input", { bubbles: true }));
}

// The exact text and location of in-flight (interim) speech rendered into a
// field, so a later revision can find and replace it.
interface InterimSpan {
  field: HTMLInputElement | HTMLTextAreaElement;
  start: number;
  end: number;
  text: string;
}

function insertNativeText(
  field: HTMLInputElement | HTMLTextAreaElement,
  text: string,
): InterimSpan {
  const start = field.selectionStart ?? field.value.length;
  const end = field.selectionEnd ?? field.value.length;
  const before = field.value.slice(0, start);
  const needsSpace =
    before.length > 0 && !/\s$/.test(before) && !/^[\n.,:;!?]/.test(text) ? " " : "";
  const insertion = needsSpace + text;
  setNativeValue(field, before + insertion + field.value.slice(end));
  const caret = start + insertion.length;
  field.setSelectionRange(caret, caret);
  return { field, start, end: caret, text: insertion };
}

// Interim text is pulled back out before each revision. Verify the span still
// holds exactly what we wrote — if a controlled component transformed the
// value or the user edited it, replacement is unsafe and the caller bails.
function removeInterim(span: InterimSpan): boolean {
  const { field, start, end, text } = span;
  if (field.value.slice(start, end) !== text) return false;
  setNativeValue(field, field.value.slice(0, start) + field.value.slice(end));
  field.setSelectionRange(start, start);
  return true;
}

// Contenteditable hosts use execCommand("insertText"), which respects the
// selection + undo stack and emits the beforeinput/input events that rich-text
// editors listen for; a Range fallback covers the rare case it returns false.
function insertText(field: EditableField, text: string) {
  if (isNativeField(field)) {
    insertNativeText(field, text);
    return;
  }

  field.focus();
  const insertion =
    (ceCaretNeedsLeadingSpace() && !/^[\n.,:;!?]/.test(text) ? " " : "") + text;
  const ok = document.execCommand("insertText", false, insertion);
  if (ok) return;

  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(insertion));
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }
  field.dispatchEvent(new InputEvent("input", { bubbles: true }));
}

// Chrome's Web Speech engine returns lowercase, unpunctuated text. Spoken
// commands ("period", "new line") are the only reliable local source of
// punctuation; capitalization follows from the resulting sentence breaks.
const PUNCTUATION_COMMANDS: Record<string, string> = {
  period: ".",
  comma: ",",
  colon: ":",
  semicolon: ";",
  "question mark": "?",
  "exclamation point": "!",
  "exclamation mark": "!",
  "new line": "\n",
  "new paragraph": "\n\n",
};
const MID_SENTENCE_PUNCTUATION = new Set([",", ":", ";"]);

// Chrome finalizes an utterance after roughly a second of silence; waiting
// this much longer before auto-inserting a period distinguishes a
// sentence-ending pause from a breath. Speech resuming cancels it.
const AUTO_PERIOD_PAUSE_MS = 2000;

interface SentenceState {
  atSentenceStart: boolean;
}

function formatTranscript(raw: string, state: SentenceState): string {
  const words = raw.split(/\s+/).filter(Boolean);
  let out = "";
  for (let i = 0; i < words.length; i++) {
    const one = words[i].toLowerCase();
    const two = i + 1 < words.length ? `${one} ${words[i + 1].toLowerCase()}` : "";
    const punctuation = PUNCTUATION_COMMANDS[two] ?? PUNCTUATION_COMMANDS[one];
    if (punctuation !== undefined) {
      if (PUNCTUATION_COMMANDS[two] !== undefined) i++;
      out += punctuation;
      if (!MID_SENTENCE_PUNCTUATION.has(punctuation)) state.atSentenceStart = true;
      continue;
    }

    let word = words[i];
    if (/^i(['’]\w+)?$/i.test(word)) word = `I${word.slice(1)}`;
    if (state.atSentenceStart) {
      word = word.charAt(0).toUpperCase() + word.slice(1);
      state.atSentenceStart = false;
    }
    out += (out === "" || /\s$/.test(out) ? "" : " ") + word;
  }
  return out;
}

function textBeforeCaret(field: EditableField): string {
  if (isNativeField(field)) {
    return field.value.slice(0, field.selectionStart ?? field.value.length);
  }
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0).cloneRange();
    range.setStart(range.startContainer, 0);
    return range.toString();
  }
  return "";
}

// Whether dictation begins at a sentence boundary, judged by the text
// preceding the caret in the target field.
function startsAtSentence(field: EditableField | null): boolean {
  if (!field) return true;
  const before = textBeforeCaret(field);
  const trimmed = before.trimEnd();
  return trimmed === "" || /[.!?]$/.test(trimmed) || /\n\s*$/.test(before);
}

export default function GlobalSpeechToText() {
  const enabled = getRecognitionCtor() !== null;

  const [field, setField] = useState<EditableField | null>(null);
  const [listening, setListening] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  const fieldRef = useRef<EditableField | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const sentenceStateRef = useRef<SentenceState>({ atSentenceStart: true });
  const interimSpanRef = useRef<InterimSpan | null>(null);
  // Set when interim replacement proved unsafe (span verification failed);
  // finals keep inserting but interim rendering stops for the session.
  const interimBrokenRef = useRef(false);
  const autoPeriodTimerRef = useRef<number | null>(null);

  const updatePosition = useCallback((el: EditableField) => {
    const r = el.getBoundingClientRect();
    setPos({ top: r.top + 4, right: Math.max(4, window.innerWidth - r.right - 4) });
  }, []);

  const clearAutoPeriodTimer = useCallback(() => {
    if (autoPeriodTimerRef.current !== null) {
      window.clearTimeout(autoPeriodTimerRef.current);
      autoPeriodTimerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    clearAutoPeriodTimer();
    recognitionRef.current?.stop();
    setListening(false);
  }, [clearAutoPeriodTimer]);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor || !fieldRef.current) return;
    sentenceStateRef.current = { atSentenceStart: startsAtSentence(fieldRef.current) };
    interimSpanRef.current = null;
    interimBrokenRef.current = false;
    const rec = new Ctor();
    rec.lang = "en-US";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      clearAutoPeriodTimer();
      // Pull the previous interim rendering out before committing finals.
      if (interimSpanRef.current) {
        if (!removeInterim(interimSpanRef.current)) interimBrokenRef.current = true;
        interimSpanRef.current = null;
      }
      let insertedFinal = false;
      let interimText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i];
        if (result.isFinal) {
          const text = formatTranscript(result[0].transcript.trim(), sentenceStateRef.current);
          if (text && fieldRef.current) {
            insertText(fieldRef.current, text);
            insertedFinal = true;
          }
        } else {
          interimText += result[0].transcript;
        }
      }
      const pending = interimText.trim();
      const target = fieldRef.current;
      if (pending && target && isNativeField(target) && !interimBrokenRef.current) {
        // Format against a copy — interim must not advance committed state.
        const preview = formatTranscript(pending, { ...sentenceStateRef.current });
        interimSpanRef.current = insertNativeText(target, preview);
      }
      // An utterance finalized with no speech behind it — start the
      // sentence-ending pause clock.
      if (insertedFinal && !pending) {
        autoPeriodTimerRef.current = window.setTimeout(() => {
          autoPeriodTimerRef.current = null;
          const f = fieldRef.current;
          if (!recognitionRef.current || !f) return;
          // An explicit "period", a keystroke, or a moved caret suppresses it.
          if (!/[A-Za-z0-9]$/.test(textBeforeCaret(f))) return;
          insertText(f, ".");
          sentenceStateRef.current.atSentenceStart = true;
        }, AUTO_PERIOD_PAUSE_MS);
      }
    };
    rec.onend = () => {
      // Recognition ended with speech still unconfirmed — keep the last
      // hypothesis in the field rather than discarding the user's words.
      clearAutoPeriodTimer();
      recognitionRef.current = null;
      interimSpanRef.current = null;
      setListening(false);
    };
    rec.onerror = () => {
      clearAutoPeriodTimer();
      recognitionRef.current = null;
      interimSpanRef.current = null;
      setListening(false);
    };
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  }, [clearAutoPeriodTimer]);

  // Track the focused field and anchor the control to it.
  useEffect(() => {
    if (!enabled) return;

    const onFocusIn = (e: FocusEvent) => {
      const target = asEditableField(e.target);
      if (!target) return;
      fieldRef.current = target;
      setField(target);
      updatePosition(target);
    };
    const onFocusOut = (e: FocusEvent) => {
      const related = e.relatedTarget as HTMLElement | null;
      if (related?.getAttribute?.("data-stt-button") === "true") return;
      if (recognitionRef.current) {
        // Dictation follows focus between editable fields; leaving them
        // entirely ends the session. fieldRef stays set so the final result
        // Chrome flushes on stop() still lands in the field.
        if (asEditableField(related)) return;
        clearAutoPeriodTimer();
        recognitionRef.current.stop();
        setListening(false);
      } else {
        fieldRef.current = null;
      }
      setField(null);
      setPos(null);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [enabled, updatePosition, clearAutoPeriodTimer]);

  // Keep the control glued to the field as the page scrolls/resizes.
  useEffect(() => {
    if (!field) return;
    const handler = () => updatePosition(field);
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [field, updatePosition]);

  useEffect(
    () => () => {
      clearAutoPeriodTimer();
      recognitionRef.current?.abort();
    },
    [clearAutoPeriodTimer],
  );

  if (!enabled || !field || !pos) return null;

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        top: pos.top,
        right: pos.right,
        zIndex: 13000,
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        pointerEvents: "none",
      }}
    >
      {listening && (
        <Box
          sx={{
            pointerEvents: "none",
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.6,
            color: "common.white",
            bgcolor: "error.main",
            boxShadow: 1,
            whiteSpace: "nowrap",
          }}
        >
          Listening…
        </Box>
      )}
      <Tooltip title={listening ? "Stop dictation" : "Dictate"}>
        <IconButton
          data-stt-button="true"
          size="small"
          // Prevent the click from blurring the target field.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => (listening ? stop() : start())}
          sx={{
            pointerEvents: "auto",
            position: "relative",
            width: 28,
            height: 28,
            color: listening ? "error.main" : "primary.main",
            bgcolor: "background.paper",
            boxShadow: 1,
            "&:hover": { bgcolor: "background.paper" },
            ...(listening && {
              "&::after": {
                content: '""',
                position: "absolute",
                inset: -2,
                borderRadius: "50%",
                border: "2px solid",
                borderColor: "error.main",
                animation: "stt-pulse 1.2s ease-out infinite",
              },
              "@keyframes stt-pulse": {
                "0%": { transform: "scale(1)", opacity: 0.7 },
                "100%": { transform: "scale(1.7)", opacity: 0 },
              },
            }),
          }}
        >
          {listening ? <MicIcon fontSize="small" /> : <MicNoneIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>,
    document.body,
  );
}
